import type { VisibleTextSnapshot } from "./uniqueReferenceChunk";

/** `[start, end)` offsets into a visible-text snapshot's `fullText`. */
export interface VisibleTextSpan {
  start: number;
  end: number;
}

/** Index of the last node starting at or before `offset`. */
function nodeIndexAt(prefix: readonly number[], offset: number): number {
  let lo = 0;
  let hi = prefix.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (prefix[mid] <= offset) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo - 1;
}

/**
 * One `StaticRange` per span, located through the snapshot's text nodes, so
 * offsets skip deleted text and deleted ghosts exactly as `fullText` does.
 * Static, not live: a live `Range` whose node React removes collapses onto the
 * parent and can cover other text, while a `StaticRange` over a removed node
 * paints nothing (ADR 0001). Empty spans and spans outside the text are dropped.
 */
export function staticRangesForSpans(
  snapshot: VisibleTextSnapshot,
  spans: Iterable<VisibleTextSpan>
): StaticRange[] {
  const { nodes, prefix, fullText } = snapshot;
  const ranges: StaticRange[] = [];
  for (const { start, end } of spans) {
    if (start < 0 || end <= start || end > fullText.length) continue;
    // The node holding the first character, and the one holding the last.
    const first = nodeIndexAt(prefix, start);
    const last = nodeIndexAt(prefix, end - 1);
    ranges.push(
      new StaticRange({
        startContainer: nodes[first],
        startOffset: start - prefix[first],
        endContainer: nodes[last],
        endOffset: end - prefix[last],
      })
    );
  }
  return ranges;
}

/**
 * The span as one `StaticRange` per visible text node it touches. One range
 * from the span's first to its last character would also cover any deleted
 * text (a `del`, a deleted ghost) between them, which the snapshot's offsets
 * skip but the browser would paint. Empty for an empty or out-of-range span.
 */
export function staticRangesPerNode(
  snapshot: VisibleTextSnapshot,
  { start, end }: VisibleTextSpan
): StaticRange[] {
  const { nodes, prefix, fullText } = snapshot;
  if (start < 0 || end <= start || end > fullText.length) return [];
  const ranges: StaticRange[] = [];
  const last = nodeIndexAt(prefix, end - 1);
  for (let i = nodeIndexAt(prefix, start); i <= last; i++) {
    const from = Math.max(start, prefix[i]) - prefix[i];
    const to = Math.min(end, prefix[i] + nodes[i].length) - prefix[i];
    if (to <= from) continue;
    ranges.push(
      new StaticRange({
        startContainer: nodes[i],
        startOffset: from,
        endContainer: nodes[i],
        endOffset: to,
      })
    );
  }
  return ranges;
}

const fitsNode = (node: Node, offset: number): boolean =>
  node.isConnected &&
  offset <=
    (node instanceof CharacterData ? node.length : node.childNodes.length);

/**
 * A live `Range` over `range`'s boundaries, to measure it for scrolling:
 * `StaticRange` has no `getBoundingClientRect`. Null when the boundaries no
 * longer fit the document, as after React removed or rewrote the text.
 */
export function liveRangeFor(range: AbstractRange): Range | null {
  if (
    !fitsNode(range.startContainer, range.startOffset) ||
    !fitsNode(range.endContainer, range.endOffset)
  ) {
    return null;
  }
  const live = document.createRange();
  live.setStart(range.startContainer, range.startOffset);
  live.setEnd(range.endContainer, range.endOffset);
  return live;
}
